import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CreditCard, Loader2, Wallet, Zap, FileText, Calendar, ChevronRight,
  Banknote, Eye, MailPlus, Percent, Trash2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function fmtChf(n) {
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

// Ouvre le PDF d'une facture dans un nouvel onglet (iter50 — uniformisation avec OrDetail).
// Avant : <a download> + click programmatique, peu fiable sur Safari/Cloudflare.
// Après : window.open(blob) + lien de secours pour téléchargement si popup bloqué.
async function openFacturePdf(factureId, numero = "facture") {
  try {
    const r = await api.get(`/factures/${factureId}/pdf`, { responseType: "blob" });
    const blob = r.data;
    const objUrl = URL.createObjectURL(blob);
    const win = window.open(objUrl, "_blank", "noopener");
    if (!win) {
      // Popup bloqué — fallback téléchargement
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${numero}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.info("Popup bloqué — le PDF a été téléchargé.");
    }
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  } catch (e) {
    toast.error(`Impossible d'ouvrir le PDF : ${e.response?.data?.detail || e.message || ""}`);
  }
}

const MODE_LABELS = {
  especes: { label: "Espèces", icon: Banknote, color: "bg-amber-50 text-amber-700 border-amber-200" },
  ayapay: { label: "AyaPay (CB / TWINT)", icon: CreditCard, color: "bg-blue-50 text-blue-700 border-blue-200" },
  escompte: { label: "Escompte", icon: Percent, color: "bg-violet-50 text-violet-700 border-violet-200" },
  // iter152 — Couverture abonnement (1150 → 4250 auto avec annulation TVA)
  abonnement: { label: "Abonnement", icon: ShieldCheck, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function Caisse() {
  const [tab, setTab] = useState("queue");
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="caisse-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Wallet className="w-7 h-7 text-[#52a675]" /> Caisse
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Encaissements comptoir · espèces, CB AyaPay et TWINT AyaPay
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        {/* iter254 — mobile : menu déroulant au lieu de la bande d'onglets */}
        <div className="sm:hidden mb-3">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full" data-testid="caisse-tab-menu">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="queue" data-testid="caisse-tab-menu-queue">À encaisser</SelectItem>
              <SelectItem value="vente-rapide" data-testid="caisse-tab-menu-vente">Vente rapide</SelectItem>
              <SelectItem value="rapport" data-testid="caisse-tab-menu-rapport">Décompte journalier</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <TabsList className="hidden sm:flex w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
          <TabsTrigger value="queue" data-testid="tab-queue"><FileText className="w-4 h-4 mr-2" /> À encaisser</TabsTrigger>
          <TabsTrigger value="vente-rapide" data-testid="tab-vente-rapide"><Zap className="w-4 h-4 mr-2" /> Vente rapide</TabsTrigger>
          <TabsTrigger value="rapport" data-testid="tab-rapport"><Calendar className="w-4 h-4 mr-2" /> Décompte journalier</TabsTrigger>
        </TabsList>
        <TabsContent value="queue" className="mt-4"><QueueTab /></TabsContent>
        <TabsContent value="vente-rapide" className="mt-4"><VenteRapideTab /></TabsContent>
        <TabsContent value="rapport" className="mt-4"><RapportTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Queue : factures à encaisser
// ============================================================
function QueueTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [encDialog, setEncDialog] = useState(null); // facture en cours d'encaissement
  const [retirerDialog, setRetirerDialog] = useState(null); // facture à retirer de caisse

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/caisse/queue");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div data-testid="queue-tab">
      <div className="mb-3 text-sm text-gray-600">
        {items.length === 0 && !loading
          ? "Aucune facture en attente d'encaissement."
          : `${items.length} facture${items.length > 1 ? "s" : ""} en attente.`}
      </div>

      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((f) => (
            <Card
              key={f.id}
              className="p-4 border-gray-200 hover:border-[#52a675] hover:shadow-md cursor-pointer transition"
              onClick={() => setEncDialog(f)}
              data-testid={`queue-item-${f.id}`}
            >
              {/* iter254 — mobile : infos puis montant en dessous (plus de chevauchement) */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono font-semibold text-sm text-gray-900">{f.numero}</span>
                    {f.or_id && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[10px]">Atelier</Badge>}
                    {f.is_hybride && (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]"
                        title={`Solde de ${fmtChf(f.solde_virement_30j)} CHF sera viré sous 30j`}
                      >
                        🔀 Hybride
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-gray-800 truncate">{f.contact_label}</div>
                  <div className="text-xs text-gray-500 truncate">{f.titre}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">Émise le {fmtDate(f.date_emission)}</div>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <div className="text-xl font-bold text-gray-900">{fmtChf(f.reste_a_payer)} <span className="text-xs text-gray-500 font-normal">CHF</span></div>
                  {f.is_hybride ? (
                    <div className="text-[11px] text-amber-700">
                      sur {fmtChf(f.montant_brut)} (solde {fmtChf(f.solde_virement_30j)} en 30j)
                    </div>
                  ) : (
                    f.montant_brut !== f.reste_a_payer && (
                      <div className="text-[11px] text-gray-400">sur {fmtChf(f.montant_brut)}</div>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openFacturePdf(f.id, f.numero); }}
                  className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition"
                  title="Voir la facture PDF"
                  data-testid={`queue-pdf-${f.id}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRetirerDialog(f); }}
                    className="p-2 rounded-md text-gray-500 hover:bg-amber-50 hover:text-amber-700 transition"
                    title="Retirer de la caisse → Envoi 30j"
                    data-testid={`queue-retirer-${f.id}`}
                  >
                    <MailPlus className="w-4 h-4" />
                  </button>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Card>
          ))}
        </div>
      )}

      <EncaisserDialog facture={encDialog} onClose={() => setEncDialog(null)} onDone={() => { setEncDialog(null); fetch(); }} />
      <RetirerCaisseDialog
        facture={retirerDialog}
        onClose={() => setRetirerDialog(null)}
        onDone={() => { setRetirerDialog(null); fetch(); }}
      />
    </div>
  );
}

// iter47 — Dialog admin : retirer une facture de la caisse pour l'envoyer en 30j
function RetirerCaisseDialog({ facture, onClose, onDone }) {
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);

  if (!facture) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/factures/${facture.id}/retirer-caisse`, {
        motif: motif.trim() || undefined,
      });
      toast.success(data.message || `Facture ${facture.numero} retirée de la caisse`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
      setMotif("");
    }
  };

  return (
    <Dialog open={!!facture} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="retirer-caisse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailPlus className="w-5 h-5 text-amber-600" />
            Retirer de la caisse
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>
            La facture <span className="font-mono font-bold">{facture.numero}</span> sortira
            de la file d'encaissement caisse et passera en envoi 30 jours.
          </p>
          {facture.montant_brut !== facture.reste_a_payer && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900" data-testid="retirer-caisse-solde">
              <strong>Acompte déjà encaissé conservé.</strong><br />
              Seul le solde de <span className="font-mono font-bold">{fmtChf(facture.reste_a_payer)} CHF</span> passe
              en 30 jours. Le paiement déjà comptabilisé et ses écritures ne bougent pas.
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            <strong>Aucune écriture comptable n'est modifiée.</strong><br />
            La facture garde son numéro et ses écritures (CO 957). Seule l'échéance est recalculée à +30j.
            Vous pourrez ensuite la télécharger en PDF et l'envoyer au client par mail ou courrier.
          </div>
          <div>
            <Label htmlFor="retirer-motif" className="text-xs">Motif (optionnel)</Label>
            <Input
              id="retirer-motif"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex: client préfère paiement à 30j"
              disabled={busy}
              data-testid="retirer-caisse-motif"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="retirer-caisse-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="retirer-caisse-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <MailPlus className="w-4 h-4 mr-1.5" />}
            Retirer et envoyer 30j
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EncaisserDialog({ facture, onClose, onDone }) {
  const [mode, setMode] = useState("especes");
  const [montant, setMontant] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (facture) {
      setMontant(facture.reste_a_payer ? String(facture.reste_a_payer) : "");
      setMode("especes");
    }
  }, [facture]);

  // iter50 — Quand on bascule en mode escompte, on pré-remplit avec le reste à
  // payer (cas typique : "solder un petit reliquat d'arrondi" → 2 CHF).
  const handleSetMode = (k) => {
    setMode(k);
    if (k === "escompte" && facture) {
      const reste = facture.reste_a_payer || 0;
      setMontant(reste > 0 ? String(reste) : "");
    }
  };

  const reste = facture?.reste_a_payer || 0;
  const escompteSeuilAlerte = Math.max(20, reste * 0.05); // 5% ou 20 CHF
  const showEscompteAlerte = mode === "escompte" && parseFloat(montant || 0) > escompteSeuilAlerte;

  const submit = async () => {
    if (!facture) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/caisse/encaisser/${facture.id}`, {
        mode,
        montant: parseFloat(montant),
        date: today(),
      });
      toast.success(`Encaissement enregistré · ${data.statut_facture}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!facture} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Encaisser {facture?.numero}</DialogTitle>
        </DialogHeader>
        {facture && (
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500 mb-1">Client</div>
              <div className="font-semibold">{facture.contact_label}</div>
              <div className="text-xs text-gray-500 mt-2 mb-1">Reste à payer</div>
              <div className="text-2xl font-bold text-[#52a675]">{fmtChf(facture.reste_a_payer)} <span className="text-sm text-gray-500 font-normal">CHF</span></div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Mode d'encaissement *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(MODE_LABELS).map(([k, m]) => {
                  const Icon = m.icon;
                  const active = mode === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => handleSetMode(k)}
                      data-testid={`mode-${k}`}
                      className={`p-3 rounded-lg border-2 transition flex flex-col items-center gap-1 ${
                        active ? "border-[#52a675] bg-emerald-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${active ? "text-[#52a675]" : "text-gray-500"}`} />
                      <span className={`text-xs font-semibold ${active ? "text-[#2d5f3f]" : "text-gray-600"}`}>{m.label}</span>
                    </button>
                  );
                })}
              </div>
              {mode === "escompte" && (
                <p className="text-[11px] text-violet-700 mt-2 leading-relaxed">
                  💡 L'escompte solde la créance sans entrée de caisse. Comptabilisé sur le
                  compte 3800 (Escomptes accordés). Usage : petit arrondi commercial.
                </p>
              )}
              {mode === "abonnement" && (
                <p className="text-[11px] text-emerald-700 mt-2 leading-relaxed" data-testid="abonnement-helper">
                  💡 Prise en charge par couverture/abonnement. Encaissement sur compte
                  1150, puis décaissement automatique vers 4250 (charge) avec annulation
                  de la TVA collectée (IP). Le CA atelier (3400/3200) reste préservé.
                </p>
              )}
              {showEscompteAlerte && (
                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                  ⚠️ Escompte élevé ({fmtChf(parseFloat(montant))} CHF). Si ce n'est pas un
                  arrondi mais un vrai rabais, modifiez plutôt la facture directement.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">
                {mode === "escompte" ? "Montant de l'escompte (CHF) *" : "Montant (CHF) *"}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                data-testid="encaisser-montant"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                {mode === "escompte"
                  ? "Le solde sera marqué payé (créance éteinte sans entrée de caisse.)"
                  : mode === "abonnement"
                  ? "Saisissez la part prise en charge par l'abonnement. Vous pouvez encaisser le reste autrement (espèces, CB, virement…)."
                  : "Vous pouvez encaisser partiellement."}
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          {facture && (
            <Button variant="outline" onClick={() => openFacturePdf(facture.id, facture.numero)} data-testid="encaisser-pdf">
              <Eye className="w-4 h-4 mr-2" /> Voir PDF
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || !montant || parseFloat(montant) <= 0} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="encaisser-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
            Encaisser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Vente rapide
// ============================================================
function VenteRapideTab() {
  const [libelle, setLibelle] = useState("");
  const [montant, setMontant] = useState("");
  const [type, setType] = useState("marchandise");
  const [mode, setMode] = useState("especes");
  const [submitting, setSubmitting] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  const submit = async () => {
    if (!libelle.trim()) { toast.error("Libellé requis"); return; }
    const m = parseFloat(montant);
    if (!m || m <= 0) { toast.error("Montant > 0 requis"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/caisse/vente-rapide", {
        libelle: libelle.trim(),
        montant_ttc: m,
        type, mode, date: today(),
      });
      toast.success(`Vente ${data.numero} · ${fmtChf(data.ttc)} CHF`);
      setLastSale(data);
      setLibelle("");
      setMontant("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vente-rapide-tab">
      <div className="md:col-span-2">
        <Card className="p-5 border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Vente comptoir rapide</h2>
          <p className="text-xs text-gray-500 mb-4">Pour les passages sans facture (ampoule, fluide, accessoire…). Une mini-facture "Client de passage" est créée automatiquement.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Libellé *</Label>
              <Input
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                placeholder="Ex : Changement ampoule phare, AdBlue 5L…"
                maxLength={120}
                data-testid="vr-libelle"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Montant TTC (CHF) *</Label>
              <Input
                type="number"
                step="0.05"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                placeholder="30.00"
                className="text-xl font-bold"
                data-testid="vr-montant"
              />
              <p className="text-[11px] text-gray-500 mt-1">TVA 8.1% calculée automatiquement.</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Type *</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "marchandise", l: "Marchandise", desc: "Compte 3200" },
                  { v: "service", l: "Prestation service", desc: "Compte 3400" },
                ].map((t) => {
                  const active = type === t.v;
                  return (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => setType(t.v)}
                      data-testid={`vr-type-${t.v}`}
                      className={`p-3 rounded-lg border-2 transition text-left ${
                        active ? "border-[#52a675] bg-emerald-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`font-semibold text-sm ${active ? "text-[#2d5f3f]" : "text-gray-900"}`}>{t.l}</div>
                      <div className="text-[11px] text-gray-500">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Mode d'encaissement *</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MODE_LABELS).map(([k, m]) => {
                  const Icon = m.icon;
                  const active = mode === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setMode(k)}
                      data-testid={`vr-mode-${k}`}
                      className={`p-3 rounded-lg border-2 transition flex flex-col items-center gap-1 ${
                        active ? "border-[#52a675] bg-emerald-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${active ? "text-[#52a675]" : "text-gray-500"}`} />
                      <span className={`text-xs font-semibold ${active ? "text-[#2d5f3f]" : "text-gray-600"}`}>{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting || !libelle || !montant}
              className="w-full bg-[#52a675] hover:bg-[#3d8a5c] py-6 text-base"
              data-testid="vr-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Encaisser {montant ? `· ${fmtChf(parseFloat(montant))} CHF` : ""}
            </Button>
          </div>
        </Card>
      </div>

      <div>
        <Card className="p-5 border-gray-200 bg-gray-50/50">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Dernière vente</h3>
          {!lastSale ? (
            <p className="text-xs text-gray-400">Aucune pour l'instant.</p>
          ) : (
            <div className="text-sm space-y-1.5" data-testid="last-sale-recap">
              <div className="font-mono font-semibold text-[#52a675]">{lastSale.numero}</div>
              <div className="text-xs text-gray-500">HT · {fmtChf(lastSale.ht)}</div>
              <div className="text-xs text-gray-500">TVA · {fmtChf(lastSale.tva)}</div>
              <div className="text-lg font-bold text-gray-900 pt-1 border-t border-gray-200">{fmtChf(lastSale.ttc)} CHF</div>
              <div className="text-[10px] text-emerald-700 mt-2">✓ Écritures comptables créées</div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => openFacturePdf(lastSale.facture_id, lastSale.numero)}
                data-testid="last-sale-pdf"
              >
                <Eye className="w-3.5 h-3.5 mr-2" /> Imprimer la facture PDF
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================
// Rapport journalier
// ============================================================
function RapportTab() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/caisse/rapport-journalier", { params: { date } });
      setData(data);
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  // iter94 — Annulation d'un encaissement (fausse manipulation) : supprime
  // l'encaissement + l'écriture comptable liée, recalcule le statut de la
  // facture (le PDF reflète automatiquement l'annulation) et remet la facture
  // dans la file Caisse si nécessaire.
  const annulerEncaissement = async (d) => {
    if (!window.confirm(
      `Annuler cet encaissement ?\n\nFacture ${d.numero} · ${fmtChf(d.montant)} CHF (${d.mode})\nClient : ${d.client || "—"}\n\n` +
      "L'écriture comptable liée sera supprimée, le statut de la facture recalculé " +
      "(le PDF reflétera l'annulation) et la facture remise dans la file Caisse si nécessaire.\n" +
      "Un audit log est conservé."
    )) return;
    try {
      await api.delete(`/factures/${d.facture_id}/encaissements/${d.encaissement_id}`);
      toast.success(`Encaissement annulé — facture ${d.numero} remise à jour`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  return (
    <div className="space-y-4" data-testid="rapport-tab">
      <Card className="p-4 border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Date :</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="rapport-date" />
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="rapport-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rafraîchir"}
        </Button>
        <div className="ml-auto text-xs text-gray-500">
          {data && `${data.detail.length} encaissement${data.detail.length > 1 ? "s" : ""} · ${fmtDate(data.date)}`}
        </div>
      </Card>

      {data && (
        <>
          {/* KPI par mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rapport-kpis">
            {Object.entries(MODE_LABELS).map(([k, m]) => {
              const Icon = m.icon;
              const total = data.totaux[k];
              const nb = data.nb_par_mode[k];
              return (
                <Card key={k} className={`p-4 border-2 ${m.color}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" />
                    <div className="text-xs font-semibold uppercase tracking-wider">{m.label}</div>
                  </div>
                  <div className="text-2xl font-bold">{fmtChf(total)} <span className="text-sm font-normal opacity-70">CHF</span></div>
                  <div className="text-[11px] mt-1 opacity-70">{nb} encaissement{nb > 1 ? "s" : ""}</div>
                </Card>
              );
            })}
          </div>

          {/* Total du jour */}
          <Card className="p-5 border-2 border-[#52a675] bg-gradient-to-br from-[#52a675]/10 to-transparent">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Total du jour</div>
                <div className="text-3xl font-bold text-gray-900 mt-1">{fmtChf(data.total_jour)} CHF</div>
              </div>
              <div className="text-right text-xs text-gray-600">
                <div>AyaPay : <strong>{fmtChf(data.totaux.ayapay)}</strong> CHF</div>
                <div className="text-[10px] mt-1 italic">Versé sur banque ultérieurement par AyaPay (-commission)</div>
              </div>
            </div>
          </Card>

          {/* Détail */}
          <Card className="border-gray-200 overflow-hidden">
            <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider font-semibold text-gray-600">
              Détail des encaissements
            </div>
            {data.detail.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">Aucun encaissement ce jour-là.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">N°</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">Client</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">Mode</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase">Montant</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {data.detail.map((d) => {
                    const m = MODE_LABELS[d.mode];
                    return (
                      <tr key={d.encaissement_id} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-mono text-xs">{d.numero}</td>
                        <td className="px-3 py-2 text-xs">
                          <div>{d.client}</div>
                          {d.origine === "vente_rapide" && <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[9px] mt-0.5">Vente rapide</Badge>}
                          {d.origine === "natif_or" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[9px] mt-0.5">Atelier</Badge>}
                        </td>
                        <td className="px-3 py-2">
                          {m && <Badge variant="outline" className={`${m.color} text-[10px]`}>{m.label}</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm">{fmtChf(d.montant)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openFacturePdf(d.facture_id, d.numero)}
                              className="p-1.5 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                              title="Voir la facture"
                              data-testid={`rapport-pdf-${d.encaissement_id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => annulerEncaissement(d)}
                              className="p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600"
                              title="Annuler cet encaissement (fausse manipulation)"
                              data-testid={`rapport-annuler-${d.encaissement_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
